import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { InventoryFilters, InventoryFiltersState } from '../components/inventory/InventoryFilters';
import { InventoryListCard } from '../components/inventory/InventoryListCard';
import { InventoryDrawer } from '../components/inventory/InventoryDrawer';
import { InventorySectionNav } from '../components/inventory/InventorySectionNav';
import { ThumbnailCard } from '../components/inventory/ThumbnailCard';
import { listInventory, InventoryItem, InventoryListParams } from '../api/inventory';
import {
  inventoryLookupLabel,
  isInventoryLookupGame,
  type InventoryLookupGame,
} from '../lib/inventoryNav';
import { colors, spacing, typography, typographyFlat, borderRadius } from '../styles/tokens';
import { ensureApiAuthenticated } from '../services/api';

const INITIAL_FILTERS: InventoryFiltersState = {
  condition: 'All',
  status: 'all',
  location: '',
  minQuantity: '',
  maxQuantity: '',
  language: 'All',
  cardNumber: '',
  sortBy: 'name',
  sortDir: 'asc',
  productName: '',
  setName: '',
  finish: 'all',
  game: 'mtg',
  minCost: '',
  maxCost: '',
  updatedAfter: '',
  updatedBefore: '',
};

interface InventoryFilterOptions {
  sets: {
    total: number;
    options: Array<{
      name: string;
      copies: number;
    }>;
  };
  locations: {
    options: string[];
  };
}

function filtersForRoute(game: InventoryLookupGame, params: URLSearchParams): InventoryFiltersState {
  return {
    ...INITIAL_FILTERS,
    game,
    productName: params.get('name') ?? '',
    setName: params.get('set') ?? '',
    cardNumber: params.get('number') ?? '',
    condition: params.get('condition') || 'All',
    language: params.get('language') || 'All',
    finish: params.get('printing') || 'all',
  };
}

function hasNarrowingFilters(filters: InventoryFiltersState): boolean {
  return Boolean(
    filters.productName
    || filters.setName
    || filters.cardNumber
    || filters.location
    || filters.minQuantity
    || filters.maxQuantity
    || filters.minCost
    || filters.maxCost
    || filters.updatedAfter
    || filters.updatedBefore
    || (filters.condition && filters.condition !== 'All')
    || (filters.status && filters.status !== 'all')
    || (filters.finish && filters.finish !== 'all')
    || (filters.language && filters.language !== 'All')
  );
}

export const InventoryLookupPage: React.FC = () => {
  const { game: routeGame } = useParams<{ game: string }>();
  const game: InventoryLookupGame = isInventoryLookupGame(routeGame) ? routeGame : 'mtg';
  const gameLabel = inventoryLookupLabel(game);
  const [searchParams] = useSearchParams();
  const searchKey = searchParams.toString();
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [total, setTotal] = useState(0);
  const [totalCopies, setTotalCopies] = useState(0);
  const [filterOptions, setFilterOptions] = useState<InventoryFilterOptions>({
    sets: { total: 0, options: [] },
    locations: { options: [] },
  });
  const [filters, setFilters] = useState<InventoryFiltersState>(() => filtersForRoute(game, searchParams));
  const [offset, setOffset] = useState(0);
  const [limit] = useState(15);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'list' | 'thumbnails'>('list');
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [scrollButtonHovered, setScrollButtonHovered] = useState(false);
  const observerTarget = useRef<HTMLDivElement>(null);
  const isLoadingRef = useRef(false);
  const itemsLengthRef = useRef(0);
  const totalRef = useRef(0);
  const offsetRef = useRef(0);
  const observerTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const queryVersionRef = useRef(0);
  const appliedRouteKey = useRef(`${game}?${searchKey}`);
  const hasFetchedRef = useRef(false);
  const autoOpenArmed = useRef(searchParams.get('open') === '1');

  const [selectedItemDetail, setSelectedItemDetail] = useState<InventoryItem | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const resetInventoryResults = useCallback(() => {
    if (observerTimeoutRef.current) {
      clearTimeout(observerTimeoutRef.current);
      observerTimeoutRef.current = null;
    }

    isLoadingRef.current = false;
    itemsLengthRef.current = 0;
    totalRef.current = 0;
    offsetRef.current = 0;

    setOffset(0);
    setItems([]);
    setTotal(0);
    setTotalCopies(0);
    setFilterOptions({
      sets: { total: 0, options: [] },
      locations: { options: [] },
    });
  }, []);

  const fetchInventory = useCallback(async (newOffset: number = 0) => {
    if (isLoadingRef.current) return;

    const requestVersion = queryVersionRef.current;

    isLoadingRef.current = true;
    setIsLoading(true);
    setError(null);

    try {
      await ensureApiAuthenticated();

      const params: InventoryListParams = {
        limit,
        offset: newOffset,
        sort_by: filters.sortBy as InventoryListParams['sort_by'],
        sort_dir: filters.sortDir as InventoryListParams['sort_dir'],
      };

      if (filters.productName) params.product_name = filters.productName;
      if (filters.setName) params.set_name = filters.setName;
      if (filters.condition && filters.condition !== 'All') params.condition = filters.condition;
      if (filters.status && filters.status !== 'all') params.status = filters.status;
      if (filters.location) params.location = filters.location;
      if (filters.minQuantity) params.min_quantity = parseInt(filters.minQuantity, 10);
      if (filters.maxQuantity) params.max_quantity = parseInt(filters.maxQuantity, 10);
      params.game = game;
      // true/false stay the legacy foil boolean. A printing stamp is exact
      // (Holofoil vs Reverse Holofoil) and must not also send foil=true.
      if (filters.finish === 'true' || filters.finish === 'false') {
        params.foil = filters.finish === 'true';
      } else if (filters.finish && filters.finish !== 'all') {
        params.printing = filters.finish;
      }
      if (filters.language && filters.language !== 'All') params.language = filters.language;
      if (filters.cardNumber) params.card_number = filters.cardNumber;
      if (filters.minCost) params.min_cost = parseFloat(filters.minCost);
      if (filters.maxCost) params.max_cost = parseFloat(filters.maxCost);
      if (filters.updatedAfter) params.acquired_after = filters.updatedAfter;
      if (filters.updatedBefore) params.acquired_before = filters.updatedBefore;

      const response = await listInventory(params);

      if (requestVersion !== queryVersionRef.current) {
        return;
      }

      let newItemsLength = response.data.length;
      if (newOffset > 0) {
        setItems((prev) => {
          newItemsLength = prev.length + response.data.length;
          return [...prev, ...response.data];
        });
      } else {
        setItems(response.data);
      }
      setTotal(response.total);
      setTotalCopies(response.total_copies ?? 0);
      setFilterOptions(response.filter_options ?? {
        sets: { total: 0, options: [] },
        locations: { options: [] },
      });
      setOffset(newOffset);

      totalRef.current = response.total;
      offsetRef.current = newOffset;
      itemsLengthRef.current = newItemsLength;
      if (newOffset === 0) {
        hasFetchedRef.current = true;
      }
    } catch (err) {
      if (requestVersion !== queryVersionRef.current) {
        return;
      }

      setError(err instanceof Error ? err.message : 'Failed to load inventory');
      if (newOffset === 0) {
        hasFetchedRef.current = true;
        setItems([]);
        setTotal(0);
        setTotalCopies(0);
        setFilterOptions({
          sets: { total: 0, options: [] },
          locations: { options: [] },
        });
      }
    } finally {
      if (requestVersion === queryVersionRef.current) {
        isLoadingRef.current = false;
        setIsLoading(false);
      }
    }
  }, [filters, game, limit]);

  useEffect(() => {
    const routeKey = `${game}?${searchKey}`;
    if (appliedRouteKey.current === routeKey) return;
    appliedRouteKey.current = routeKey;
    hasFetchedRef.current = false;
    autoOpenArmed.current = new URLSearchParams(searchKey).get('open') === '1';
    queryVersionRef.current += 1;
    setIsDrawerOpen(false);
    setSelectedItemDetail(null);
    setFilters(filtersForRoute(game, new URLSearchParams(searchKey)));
    resetInventoryResults();
  }, [game, searchKey, resetInventoryResults]);

  useEffect(() => {
    fetchInventory(0);
  }, [fetchInventory]);

  useEffect(() => {
    if (!autoOpenArmed.current || isLoading || !hasFetchedRef.current) return;
    autoOpenArmed.current = false;
    if (items.length === 0) return;
    setSelectedItemDetail(items[0]);
    setIsDrawerOpen(true);
  }, [isLoading, items]);

  useEffect(() => {
    if (!selectedItemDetail) return;
    const next = items.find((item) => item.id === selectedItemDetail.id);
    if (next && next !== selectedItemDetail) {
      setSelectedItemDetail(next);
    }
  }, [items, selectedItemDetail]);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          !isLoadingRef.current &&
          itemsLengthRef.current < totalRef.current &&
          offsetRef.current + limit < totalRef.current
        ) {
          if (observerTimeoutRef.current) {
            clearTimeout(observerTimeoutRef.current);
          }
          observerTimeoutRef.current = setTimeout(() => {
            fetchInventory(offsetRef.current + limit);
            observerTimeoutRef.current = null;
          }, 100);
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => {
      if (observerTimeoutRef.current) {
        clearTimeout(observerTimeoutRef.current);
      }
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
    };
  }, [fetchInventory, limit]);

  const handleFiltersChange = (newFilters: InventoryFiltersState) => {
    queryVersionRef.current += 1;
    setFilters({ ...newFilters, game });
    resetInventoryResults();
  };

  const handleFiltersReset = () => {
    queryVersionRef.current += 1;
    setFilters({ ...INITIAL_FILTERS, game });
    resetInventoryResults();
  };

  const handleEditItem = useCallback(async (itemId: string) => {
    const item = items.find((i) => i.id === itemId);
    if (item) {
      setSelectedItemDetail(item);
      setIsDrawerOpen(true);
    } else {
      setError('Item not found');
    }
  }, [items]);

  const handleDrawerClose = useCallback(() => {
    setIsDrawerOpen(false);
    setSelectedItemDetail(null);
  }, []);

  const handleDrawerSaved = useCallback(() => {
    queryVersionRef.current += 1;
    resetInventoryResults();
    fetchInventory(0);
  }, [fetchInventory, resetInventoryResults]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <>
      <div style={styles.container}>
        <div style={styles.header}>
          <h1 style={styles.title}>{gameLabel} Lookup</h1>
          <p style={styles.subtitle}>
            {gameLabel} stock only. Add, History, and Parse stay shared across games.
          </p>
          <InventorySectionNav />
        </div>

        {error && (
          <div style={styles.error}>
            <strong>Error:</strong> {error}
          </div>
        )}

        <div style={styles.content}>
          <InventoryFilters
            filters={filters}
            scopedGame={game}
            filterOptions={filterOptions}
            onChange={handleFiltersChange}
            onReset={handleFiltersReset}
          />

          <div style={styles.results}>
            <div style={styles.resultsHeader}>
              <div style={styles.resultsCount}>
                {isLoading && offset === 0
                  ? 'Loading...'
                  : `${items.length} of ${total} ${gameLabel} items loaded • ${totalCopies} total copies`}
              </div>
              <div style={styles.resultsControls}>
                <div style={styles.viewToggle}>
                  <button
                    style={{
                      ...styles.viewToggleButton,
                      ...(viewMode === 'list' ? styles.viewToggleButtonActive : {}),
                    }}
                    onClick={() => setViewMode('list')}
                  >
                    List
                  </button>
                  <button
                    style={{
                      ...styles.viewToggleButton,
                      ...(viewMode === 'thumbnails' ? styles.viewToggleButtonActive : {}),
                    }}
                    onClick={() => setViewMode('thumbnails')}
                  >
                    Thumbnails
                  </button>
                </div>
              </div>
            </div>

            {!isLoading && !error && items.length === 0 && (
              <div style={styles.emptyState}>
                <div style={styles.emptyStateTitle}>
                  {hasNarrowingFilters(filters)
                    ? `No ${gameLabel} matches these filters`
                    : `No ${gameLabel} in stock`}
                </div>
                <div style={styles.emptyStateBody}>
                  {hasNarrowingFilters(filters)
                    ? `Try clearing a filter. This page only lists ${gameLabel} stock.`
                    : `Copies from other games stay on their own Lookup pages.`}
                </div>
              </div>
            )}

            {viewMode === 'list' ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
                {items.map((item) => (
                  <InventoryListCard
                    key={item.id}
                    item={item}
                    onEdit={handleEditItem}
                  />
                ))}
              </div>
            ) : (
              <div style={styles.thumbnailGrid}>
                {items.map((item) => (
                  <ThumbnailCard
                    key={item.id}
                    item={item}
                    onEdit={handleEditItem}
                    isButtonHovered={scrollButtonHovered}
                  />
                ))}
              </div>
            )}

            <div ref={observerTarget} style={styles.observerTarget} />
            {isLoading && offset > 0 && <div style={styles.loadingIndicator}>Loading more...</div>}
          </div>
        </div>
      </div>

      <InventoryDrawer
        item={selectedItemDetail}
        isOpen={isDrawerOpen}
        onClose={handleDrawerClose}
        onSaved={handleDrawerSaved}
      />

      {showScrollTop && (
        <button
          onClick={scrollToTop}
          style={{
            ...styles.scrollToTopButton,
            backgroundColor: scrollButtonHovered ? colors.primaryDark : colors.primary,
            transform: scrollButtonHovered ? 'scale(1.1)' : 'scale(1)',
            boxShadow: scrollButtonHovered
              ? `0 6px 20px ${colors.text}44`
              : `0 4px 12px ${colors.text}33`,
          }}
          onMouseEnter={() => setScrollButtonHovered(true)}
          onMouseLeave={() => setScrollButtonHovered(false)}
          aria-label="Scroll to top"
        >
          ↑
        </button>
      )}
    </>
  );
};

const styles = {
  container: {
    padding: spacing.xl,
  },
  header: {
    marginBottom: spacing.xl,
  },
  title: {
    margin: 0,
    fontSize: typography.h1.fontSize,
    fontWeight: typography.h1.fontWeight,
    color: colors.text,
  },
  subtitle: {
    margin: `${spacing.sm} 0 0`,
    fontSize: typographyFlat.fontSize.lg,
    color: colors.textSecondary,
  },
  error: {
    padding: spacing.md,
    marginBottom: spacing.lg,
    backgroundColor: `${colors.error}11`,
    border: `1px solid ${colors.error}`,
    borderRadius: borderRadius.sm,
    color: colors.error,
    fontSize: typographyFlat.fontSize.sm,
  },
  content: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: spacing.xl,
  },
  results: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: spacing.md,
    overflowAnchor: 'auto',
  } as React.CSSProperties,
  resultsHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: spacing.md,
    flexWrap: 'wrap' as const,
  },
  resultsCount: {
    fontSize: typographyFlat.fontSize.md,
    fontWeight: typographyFlat.fontWeight.medium,
    color: colors.textSecondary,
    flex: '1 1 280px',
  },
  resultsControls: {
    display: 'flex',
    gap: spacing.sm,
    alignItems: 'center',
    flexWrap: 'wrap' as const,
    justifyContent: 'flex-end',
    flex: '1 1 260px',
  },
  viewToggle: {
    display: 'flex',
    gap: spacing.sm,
    borderRadius: borderRadius.sm,
    backgroundColor: colors.background,
    padding: spacing.xs,
  },
  viewToggleButton: {
    padding: `${spacing.xs} ${spacing.md}`,
    fontSize: typographyFlat.fontSize.sm,
    fontWeight: typographyFlat.fontWeight.medium,
    color: colors.textSecondary,
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: borderRadius.xs,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  } as React.CSSProperties,
  viewToggleButtonActive: {
    color: colors.primary,
    backgroundColor: colors.surface,
  },
  thumbnailGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: spacing.lg,
  },
  observerTarget: {
    height: '20px',
  },
  loadingIndicator: {
    padding: spacing.lg,
    textAlign: 'center' as const,
    color: colors.textSecondary,
    fontSize: typographyFlat.fontSize.sm,
  },
  emptyState: {
    padding: spacing.lg,
    border: `1px dashed ${colors.border}`,
    borderRadius: borderRadius.md,
    backgroundColor: colors.background,
    textAlign: 'center' as const,
  },
  emptyStateTitle: {
    fontSize: typographyFlat.fontSize.md,
    fontWeight: typographyFlat.fontWeight.semibold,
    color: colors.text,
    marginBottom: spacing.xs,
  },
  emptyStateBody: {
    fontSize: typographyFlat.fontSize.sm,
    color: colors.textSecondary,
  },
  scrollToTopButton: {
    position: 'fixed' as const,
    bottom: spacing['2xl'],
    right: spacing['2xl'],
    width: '56px',
    height: '56px',
    borderRadius: '50%',
    backgroundColor: colors.primary,
    color: colors.white,
    border: 'none',
    fontSize: '24px',
    fontWeight: typographyFlat.fontWeight.bold,
    cursor: 'pointer',
    boxShadow: `0 4px 12px ${colors.text}33`,
    transition: 'all 0.3s ease',
    zIndex: 1000,
  } as React.CSSProperties,
};

export default InventoryLookupPage;
